/**
 * GranulloGlassContext.jsx - Structural Overlay State Management
 * 
 * FOUNDER DIRECTIVE: Granullo Glass — Structural Overlay Mode
 * 
 * Granullo Glass is an optional overlay mode that reveals:
 * - Dependency connections
 * - Exposure topology
 * - Structural linkages between components
 * - Decision propagation paths
 * 
 * When Glass mode is active, the interface subtly displays
 * the structural relationships across the system.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Context
const GranulloGlassContext = createContext(null);

// Glass overlay configuration
export const GLASS_CONFIG = {
  // Visual settings
  connectionOpacity: 0.15,
  nodeGlowIntensity: 0.3,
  pulseSpeed: 2000,
  
  // Animation settings
  cascadeDelay: 50,
  cascadeDuration: 800,
  
  // Colors (using CSS variables for theme compatibility)
  connectionColor: 'rgba(88, 166, 255, 0.3)',
  nodeGlowColor: 'rgba(88, 166, 255, 0.2)',
  cascadeColor: 'rgba(88, 166, 255, 0.6)',
  
  // Topology display options
  showDependencies: true,
  showExposureTopology: true,
  showPropagationPaths: true,
};

// Provider Component
export function GranulloGlassProvider({ children }) {
  // Load initial state from localStorage
  const [isGlassActive, setIsGlassActive] = useState(() => {
    try {
      const stored = localStorage.getItem('granullo_glass_mode');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Cascade animation state
  const [cascadeState, setCascadeState] = useState({
    isActive: false,
    sourceNode: null,
    affectedNodes: [],
    progress: 0
  });

  // Lens highlight state (for visual confirmation pulse)
  const [lensHighlight, setLensHighlight] = useState({
    isActive: false,
    element: null,
    zoneId: null
  });

  // Toggle Glass mode
  const toggleGlass = useCallback(() => {
    setIsGlassActive(prev => {
      const newValue = !prev;
      try {
        localStorage.setItem('granullo_glass_mode', String(newValue));
      } catch {}
      return newValue;
    });
  }, []);

  // Set specific mode
  const setGlassMode = useCallback((value) => {
    setIsGlassActive(value);
    try {
      localStorage.setItem('granullo_glass_mode', String(value));
    } catch {}
  }, []);

  // Trigger cascade animation (called when simulation runs)
  const triggerCascade = useCallback((sourceNode, affectedNodes = []) => {
    setCascadeState({
      isActive: true,
      sourceNode,
      affectedNodes,
      progress: 0
    });

    // Animate cascade progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      if (progress >= 100) {
        clearInterval(interval);
        setCascadeState(prev => ({ ...prev, isActive: false, progress: 100 }));
        // Reset after animation completes
        setTimeout(() => {
          setCascadeState({
            isActive: false,
            sourceNode: null,
            affectedNodes: [],
            progress: 0
          });
        }, 500);
      } else {
        setCascadeState(prev => ({ ...prev, progress }));
      }
    }, GLASS_CONFIG.cascadeDelay);

    return () => clearInterval(interval);
  }, []);

  // Trigger lens highlight (called when Lens resolves a component)
  const triggerLensHighlight = useCallback((element, zoneId) => {
    if (!element) return;

    setLensHighlight({
      isActive: true,
      element,
      zoneId
    });

    // Auto-clear after pulse animation
    setTimeout(() => {
      setLensHighlight({
        isActive: false,
        element: null,
        zoneId: null
      });
    }, 1200);
  }, []);

  // Clear lens highlight
  const clearLensHighlight = useCallback(() => {
    setLensHighlight({
      isActive: false,
      element: null,
      zoneId: null
    });
  }, []);

  // Keyboard shortcut: Ctrl/Cmd + Shift + G to toggle Glass
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'G') {
        e.preventDefault();
        toggleGlass();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleGlass]);

  const value = {
    // State
    isGlassActive,
    cascadeState,
    lensHighlight,
    config: GLASS_CONFIG,
    
    // Actions
    toggleGlass,
    setGlassMode,
    enableGlass: () => setGlassMode(true),
    disableGlass: () => setGlassMode(false),
    triggerCascade,
    triggerLensHighlight,
    clearLensHighlight,
  };

  return (
    <GranulloGlassContext.Provider value={value}>
      {children}
    </GranulloGlassContext.Provider>
  );
}

// Hook to use Granullo Glass
export function useGranulloGlass() {
  const context = useContext(GranulloGlassContext);
  if (!context) {
    // Return safe defaults if not in provider (for standalone usage)
    return {
      isGlassActive: false,
      cascadeState: { isActive: false, sourceNode: null, affectedNodes: [], progress: 0 },
      lensHighlight: { isActive: false, element: null, zoneId: null },
      config: GLASS_CONFIG,
      toggleGlass: () => {},
      setGlassMode: () => {},
      enableGlass: () => {},
      disableGlass: () => {},
      triggerCascade: () => {},
      triggerLensHighlight: () => {},
      clearLensHighlight: () => {},
    };
  }
  return context;
}

export default GranulloGlassContext;
